// Customer requests: the lawyer asks the customer for one thing (a newer
// document, a new signature, an answer) and the customer answers on a page in
// the app. Contract: coepi-bold docs/customer-requests/contract.md. Same
// conventions as the other case endpoints; the shared helper lives in ./client.

import { request } from './client';

export { ApiError } from './client';

export type CustomerRequestReason =
  | 'proof_of_address'
  | 'passport_photo'
  | 'id_back'
  | 'sign_again'
  | 'details'
  | 'child_document'
  | 'financas_letter'
  | 'question';

export type CustomerRequestSource = 'lawyer' | 'financas';

export type CustomerRequestStatus = 'open' | 'answered' | 'accepted' | 'cancelled';

export type CustomerRequestCheckStatus = 'checking' | 'accepted' | 'not_accepted' | 'unsure';

export type ChildDocumentKind = 'birth_certificate' | 'child_id';

export type CloseOutcome = 'accepted' | 'cancelled';

/** The case fields a `details` request may propose a correction for. */
export const DETAIL_FIELDS = [
  'first_name',
  'middle_name',
  'last_name',
  'street',
  'postal_code',
  'city',
] as const;

export type DetailField = typeof DETAIL_FIELDS[number];

export type ProposedDetails = Partial<Record<DetailField, string>>;

export interface CustomerRequestCheck {
  status: CustomerRequestCheckStatus;
  reason?: string | null;
  file_name?: string | null;
  updated_at?: string | null;
}

/**
 * What the customer sent. One shape per reason (see the contract), so every
 * field is optional here and the panel reads the ones its reason carries.
 */
export interface CustomerRequestResponse {
  // Document reasons, and an optional file on a question.
  file_name?: string | null;
  file_type?: string | null;
  // sign_again
  submission_id?: number | null;
  signed_at?: string | null;
  // details
  confirmed?: boolean;
  values?: Record<string, string | null | undefined>;
  changed?: string[];
  /** What the case said before a correction, for every changed field. */
  previous?: Record<string, string | null | undefined>;
  // financas_letter
  open_letter?: boolean;
  // question
  text?: string | null;
  choice?: string | null;
}

export interface CustomerRequest {
  id: string;
  case_id: string;
  reason: CustomerRequestReason;
  note: string;
  source: CustomerRequestSource;
  options?: string[];
  proposed?: ProposedDetails | null;
  child_document?: ChildDocumentKind | null;
  status: CustomerRequestStatus;
  created_at?: string | null;
  created_by?: string | null;
  created_by_name?: string | null;
  user_id?: string | null;
  notify?: boolean;
  mail_sent_at?: string | null;
  check?: CustomerRequestCheck | null;
  response?: CustomerRequestResponse | null;
  answered_at?: string | null;
  sent_anyway?: boolean;
  reminded_at?: string | null;
  reminder_count?: number;
  closed_at?: string | null;
  closed_by?: string | null;
  signing_submission_id?: number | null;
}

export interface CreateCustomerRequestInput {
  reason: CustomerRequestReason;
  note: string;
  source?: CustomerRequestSource;
  options?: string[];
  proposed?: ProposedDetails;
  child_document?: ChildDocumentKind;
}

const casePath = (caseId: string) => `/cases/${encodeURIComponent(caseId)}/customer-requests`;

// Newest first, every status. Works with the feature flag off, so a case that
// had requests before the flag was turned off still shows them.
export const listCustomerRequests = async (token: string, caseId: string): Promise<CustomerRequest[]> => {
  const data = await request(token, casePath(caseId), { method: 'GET' }, 'Failed to load the requests.');
  return Array.isArray(data?.requests) ? data.requests : [];
};

// 404 with the flag off, 400 on validation, 409 when an open request with the
// same reason exists. The backend's `detail` is shown as is.
export const createCustomerRequest = async (
  token: string,
  caseId: string,
  input: CreateCustomerRequestInput
): Promise<CustomerRequest> =>
  request(
    token,
    casePath(caseId),
    { method: 'POST', body: JSON.stringify(input) },
    'Failed to send the request.'
  );

export const closeCustomerRequest = async (
  token: string,
  caseId: string,
  requestId: string,
  outcome: CloseOutcome
): Promise<CustomerRequest> =>
  request(
    token,
    `${casePath(caseId)}/${encodeURIComponent(requestId)}/close`,
    { method: 'POST', body: JSON.stringify({ outcome }) },
    outcome === 'accepted' ? 'Failed to accept the answer.' : 'Failed to cancel the request.'
  );
